import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CalendarDays, MapPin, PawPrint, PlusCircle, Truck, CreditCard, CheckCircle2, X, Repeat } from "lucide-react";

const STATUS_LABELS = {
  scheduled: "Scheduled", en_route: "Van on the way!",
  in_progress: "In grooming", completed: "Completed", cancelled: "Cancelled",
};
const STATUS_TINT = {
  scheduled: "bg-blue-50 text-blue-700 border-blue-200",
  en_route: "bg-amber-50 text-amber-800 border-amber-200",
  in_progress: "bg-orange-50 text-orange-800 border-orange-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function ClientHome() {
  const [appts, setAppts] = useState([]);
  const load = () => api.get("/appointments").then(({ data }) => setAppts(data));
  useEffect(() => { load(); }, []);

  const cancel = async (a) => {
    const hasSeries = !!a.recurring_series_id;
    if (!window.confirm("Cancel this appointment?")) return;
    let scope = "one";
    if (hasSeries) {
      const wantSeries = window.confirm(
        "This is a recurring visit. Click OK to cancel the WHOLE remaining series, or Cancel to only skip this one."
      );
      scope = wantSeries ? "series" : "one";
    }
    try {
      const { data } = await api.delete(`/appointments/${a.id}?scope=${scope}`);
      toast.success(scope === "series"
        ? `Cancelled ${data.cancelled || 0} visits in your series`
        : "Appointment cancelled");
      load();
    } catch { toast.error("Cancel failed"); }
  };
  const upcoming = appts.filter(a => a.status !== "cancelled" && a.status !== "completed" && new Date(a.start) >= new Date(Date.now()-3600e3));
  const past = appts.filter(a => a.status === "completed").slice(0, 5);
  const active = appts.find(a => a.status === "en_route" || a.status === "in_progress");
  const unpaid = appts.filter(a => a.status === "completed" && !a.paid);

  return (
    <div className="p-4 md:p-8 space-y-6">
      <header>
        <div className="label-accent">Your dashboard</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Welcome back</h1>
      </header>

      {unpaid.length > 0 && (
        <section className="space-y-3" data-testid="unpaid-section">
          {unpaid.map(a => (
            <div key={a.id} className="bg-[#C85A17] text-white rounded-3xl p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-3 md:gap-5 shadow-md">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-12 h-12 rounded-2xl bg-white/15 grid place-content-center">
                  <CheckCircle2 className="w-6 h-6"/>
                </div>
                <div className="min-w-0">
                  <div className="label-accent text-amber-200">Groom complete</div>
                  <div className="font-display text-lg font-bold truncate">
                    {a.service_name} · {a.pet_name || "your pup"}
                  </div>
                  <div className="text-xs opacity-90">Amount due: ${a.price.toFixed(2)}</div>
                </div>
              </div>
              <Link to={`/pay/${a.id}`} data-testid={`btn-pay-${a.id}`}>
                <Button className="rounded-full bg-white text-[#C85A17] hover:bg-amber-50 btn-tactile h-11 px-5 font-bold">
                  <CreditCard className="w-4 h-4 mr-2"/> Pay + Tip
                </Button>
              </Link>
            </div>
          ))}
        </section>
      )}

      {active && (
        <div className="bg-[#2D4A3E] text-white rounded-3xl p-6 relative overflow-hidden grain" data-testid="active-tracker">
          <div className="label-accent text-amber-300 flex items-center gap-1"><Truck className="w-3 h-3"/> Live</div>
          <h2 className="font-display text-2xl font-bold mt-1">{STATUS_LABELS[active.status]}</h2>
          <p className="text-sm mt-2 opacity-90">
            {active.service_name} for {active.pet_name || "your pup"} · {new Date(active.start).toLocaleString()}
          </p>
        </div>
      )}

      <div className="flex gap-3">
        <Link to="/client/book" className="flex-1" data-testid="home-book">
          <Button className="w-full rounded-full bg-[#C85A17] hover:bg-[#A9480F] btn-tactile h-12">
            <PlusCircle className="w-4 h-4 mr-2"/> Book appointment
          </Button>
        </Link>
        <Link to="/client/messages" data-testid="home-chat">
          <Button variant="outline" className="rounded-full border-[#2D4A3E] text-[#2D4A3E] h-12 px-6">
            Message groomer
          </Button>
        </Link>
      </div>

      <section>
        <h2 className="font-display text-xl font-bold mb-3">Upcoming</h2>
        {upcoming.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#E5DCCE] p-8 text-center text-[#6B625B]">
            <CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-40"/>
            <p className="text-sm">No upcoming appointments</p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="upcoming-list">
            {upcoming.map(a => (
              <li key={a.id} className="bg-white rounded-2xl border border-[#E5DCCE] p-4 flex items-center gap-4">
                <div className="w-14 text-center">
                  <div className="font-display font-bold text-lg">{new Date(a.start).getDate()}</div>
                  <div className="text-[10px] uppercase text-[#6B625B]">
                    {new Date(a.start).toLocaleDateString("en-US", { month: "short" })}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-1.5">
                    {a.service_name} · {a.pet_name}
                    {a.recurring_series_id && <Repeat className="w-3 h-3 text-[#C85A17]"/>}
                  </div>
                  <div className="text-xs text-[#6B625B]">
                    {new Date(a.start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · ${a.price}
                  </div>
                  {a.address && (
                    <div className="text-xs text-[#6B625B] flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3"/> {a.address}
                    </div>
                  )}
                </div>
                <span className={`pill border text-[10px] ${STATUS_TINT[a.status]}`}>{STATUS_LABELS[a.status]}</span>
                <Button size="icon" variant="ghost" onClick={() => cancel(a)}
                  className="text-rose-600 hover:bg-rose-50 h-8 w-8"
                  data-testid={`cancel-upcoming-${a.id}`} title="Cancel">
                  <X className="w-4 h-4"/>
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {past.length > 0 && (
        <section>
          <h2 className="font-display text-xl font-bold mb-3">History</h2>
          <ul className="space-y-2">
            {past.map(a => (
              <li key={a.id} className="rounded-xl border border-[#E5DCCE] p-3 flex items-center gap-3 bg-white text-sm">
                <PawPrint className="w-4 h-4 text-[#C85A17]"/>
                <div className="flex-1">
                  <div className="font-medium">{a.service_name} · {a.pet_name}</div>
                  <div className="text-xs text-[#6B625B]">{new Date(a.start).toLocaleDateString()}</div>
                </div>
                <span className={`pill border text-[10px] ${a.paid ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                  {a.paid ? `Paid $${a.paid_amount?.toFixed(2)}` : "Awaiting payment"}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
